import React, {useCallback, useMemo, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {GRANT_STATUS_CAPTIONS} from 'src/modules/entitlements/constants';

import Button from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Dropdown} from '@pb/kit-component-dropdown';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {pluralize} from '@pb/kit-formatters';


import {CONFLICT_TOOL_TITLE, TOOL_TITLE} from './constants';
import {useGetUserEntitlementsConflicts} from './useGetUserEntitlementsConflicts';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {UpdateUserEntitlementsRelation} from 'src/openapi/admin/models/UpdateUserEntitlementsRelation';


type Props = {
    userId: number,
    ids: (string | number)[],
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UpdateUserEntitlementsRelation>,
};

const MODAL_WIDTH = 800;

export const AssignUserEntitlementsModal = ({userId, ids, onCloseModal, processAction}: Props) => {
    const grantIds = useMemo(
        () => [...new Set(ids.map(id => JSON.parse(id.toString()).grantId).filter(Boolean))],
        [ids],
    );

    const roleIds = useMemo(
        () => ids.map(id => JSON.parse(id.toString()).roleId).filter(Boolean),
        [ids],
    );

    const {
        isLoading,
        loadingError,
        data: conflicts,
    } = useGetUserEntitlementsConflicts({
        userId,
        entitlementConflictRequest: {
            roleIds,
            grantIds,
        },
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
    });
    const {currentPage, pageSize} = pagination;

    const assign = useCallback(async (options?: ActionOptions) => {
        return await processAction({
            userId,
            roleIds: roleIds.length ? roleIds : undefined,
            grantIds: grantIds.length ? grantIds : undefined,
        }, options);
    }, [roleIds, grantIds, userId, processAction]);

    const warningView = useMemo(() => (
        <div>
            Are you sure you want to assign selected {[
                roleIds.length ? pluralize('role[s]', roleIds.length) : '',
                grantIds.length ? pluralize('grant[s]', grantIds.length) : '',
            ].filter(Boolean).join(' and ')} to the user?
        </div>
    ), [roleIds, grantIds]);

    const conflictsView = useMemo(() => (
        <Group direction="v">
            <div>
                You have selected conflicting grants on {pluralize('resource[s]', conflicts.length)}
            </div>
            <Table>
                <Head>
                    <Cell>Selected Resource</Cell>
                    <Cell>Selected Grant</Cell>
                    <Cell width={80}>Status</Cell>
                    <Cell>Conflicting Grant</Cell>
                    <Cell width={80}>Status</Cell>
                </Head>
                <Body>
                    {conflicts
                        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                        .map(({resource, ...other}, index) => (
                            <Row key={(currentPage - 1) * pageSize + index + 1}>
                                <Cell>
                                    <EllipsisTextHint title={resource.name}>
                                        {resource.name}
                                    </EllipsisTextHint>
                                </Cell>
                                <Cell width={200}>
                                    {other.assignableRole ? (
                                        <Group>
                                            <Dropdown
                                                horizontalOffset={80}
                                                hoverEnabled={true}
                                                anchor={({anchorRef, getAnchorProps}) => (
                                                    <span ref={anchorRef} {...getAnchorProps()}>
                                                        <Icon type={Icons.TREE}/>
                                                    </span>
                                                )}>
                                                <div>
                                                    <Label><b>Role</b></Label>
                                                    <WidthItem maxWidth={300}>
                                                        <EllipsisTextHint title={other.assignableRole.name}>
                                                            {other.assignableRole.name}
                                                        </EllipsisTextHint>
                                                    </WidthItem>
                                                </div>
                                            </Dropdown>
                                            <WidthItem width={160}>
                                                <EllipsisTextHint title={other.assignableGrant.name}>
                                                    {other.assignableGrant.name}
                                                </EllipsisTextHint>
                                            </WidthItem>
                                        </Group>
                                    ) : (
                                        <EllipsisTextHint title={other.assignableGrant.name}>
                                            {other.assignableGrant.name}
                                        </EllipsisTextHint>
                                    )}
                                </Cell>
                                <Cell>
                                    {GRANT_STATUS_CAPTIONS[other.assignableGrant.status]}
                                </Cell>
                                <Cell width={200}>
                                    {other.conflictingRole ? (
                                        <Group>
                                            <Dropdown
                                                horizontalOffset={80}
                                                hoverEnabled={true}
                                                anchor={({anchorRef, getAnchorProps}) => (
                                                    <span ref={anchorRef} {...getAnchorProps()}>
                                                        <Icon type={Icons.TREE}/>
                                                    </span>
                                                )}>
                                                <div>
                                                    <Label><b>Role</b></Label>
                                                    <WidthItem maxWidth={300}>
                                                        <EllipsisTextHint title={other.conflictingRole.name}>
                                                            {other.conflictingRole.name}
                                                        </EllipsisTextHint>
                                                    </WidthItem>
                                                </div>
                                            </Dropdown>
                                            <WidthItem width={160}>
                                                <EllipsisTextHint title={other.conflictingGrant.name}>
                                                    {other.conflictingGrant.name}
                                                </EllipsisTextHint>
                                            </WidthItem>
                                        </Group>
                                    ) : (
                                        <EllipsisTextHint title={other.conflictingGrant.name}>
                                            {other.conflictingGrant.name}
                                        </EllipsisTextHint>
                                    )}
                                </Cell>
                                <Cell>
                                    {GRANT_STATUS_CAPTIONS[other.conflictingGrant.status]}
                                </Cell>
                            </Row>
                        ))}
                </Body>
            </Table>
            <PaginationStripe
                pageSize={pageSize}
                totalCount={conflicts.length}
                currentPage={currentPage}
                onPaginationChange={setPagination}
                goToPage={true}
                sizeSwitcherPosition="LEFT"
                sizeSwitcherType="ONLY_PAGE_SIZE"
                sizes={[5, 10]}
            />
        </Group>
    ), [conflicts, currentPage, pageSize]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {conflicts.length ? CONFLICT_TOOL_TITLE : TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                <Skeleton shown={isLoading} view={<Loading fast={true}/>}>
                    <ServerResponseHandler serverError={loadingError} smallView={true} sectionName="Assign Resources">
                        {conflicts.length ? conflictsView : warningView}
                    </ServerResponseHandler>
                </Skeleton>
            </DialogMain>
            <BulkActionFooter action={conflicts.length ? 'Confirm' : 'Assign'} onConfirm={assign}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
